import {
    Button,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { ChangeEvent } from "react"
import { NexPageSize, nexPageSizes } from "../../components/Table/pageable-table/NexPageSize"
import { ReceiptShippingInfoDto } from "../../models/DataReceipt"
import { Order } from "../../models/SortOrder"
import { transformShippingInfoDtoToSelectedShippingInfo } from "../../utils/DataReceiptUtils"
import { useDataReceiptStore } from "../../zstore/DataReceiptStore"
import { SelectMissionsOrder } from "../../zstore/DataReceiptTableSlice"

interface SelectMissionsTableProps {
    shippingInfo?: ReceiptShippingInfoDto[]
    totalShippingInfoCount?: number
    isShippingInfoDataLoading: boolean
}

export const SelectMissionsTable = (props: SelectMissionsTableProps) => {
    const { shippingInfo, totalShippingInfoCount, isShippingInfoDataLoading } = props

    const page = useDataReceiptStore((state) => state.page)
    const setPage = useDataReceiptStore((state) => state.setPage)
    const pageSize = useDataReceiptStore((state) => state.pageSize)
    const setPageSize = useDataReceiptStore((state) => state.setPageSize)
    const tableIsLoading = useDataReceiptStore((state) => state.tableIsLoading)
    const setSelectedShippingInfoWrappers = useDataReceiptStore((state) => state.setSelectedShippingInfoWrappers)
    const selectedShippingInfoWrappers = useDataReceiptStore((state) => state.selectedShippingInfoWrappers)

    const handlePageChange = (page: number) => {
        setPage(page)
    }
    const handleRowsPerPageChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setPageSize(+event.target.value as NexPageSize)
    }

    const handleRowSelect = (row: ReceiptShippingInfoDto) => {
        if (shippingInfo) {
            const oldWrappers = selectedShippingInfoWrappers.slice()

            const transformedRow = transformShippingInfoDtoToSelectedShippingInfo(row, oldWrappers.length + 1)
            setSelectedShippingInfoWrappers([...oldWrappers, transformedRow])
        }
    }

    return (
        <Paper>
            <TableContainer
                sx={{
                    maxHeight: 600,
                    td: { whiteSpace: "nowrap" },
                    overflowY: "auto",
                }}
            >
                <Table stickyHeader={true} size="small">
                    <TableHead>
                        <TableRow sx={{ "& .td": { padding: 0 } }}>
                            <TableCell>Add</TableCell>
                            <TableCell>Mission</TableCell>
                            <TableCell>Project</TableCell>
                            <TableCell>Project Number</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Tracking Number</TableCell>
                            <TableCell>Office</TableCell>
                            <TableCell>Plane</TableCell>
                            <SortableSelectMissionsTableCell orderBy={SelectMissionsOrder.FLOWN_DATE}>
                                Flown Date
                            </SortableSelectMissionsTableCell>
                            <TableCell>Operator</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(isShippingInfoDataLoading || tableIsLoading) && (
                            <TableRow>
                                <TableCell padding="none" colSpan={10}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        )}
                        {(!shippingInfo || shippingInfo.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={10}>
                                    <Typography>No Results on page {page + 1}</Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {shippingInfo?.map((info) => {
                            const mission = info.mission
                            return (
                                <TableRow key={info.id}>
                                    <TableCell>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleRowSelect(info)}
                                            disabled={
                                                !!selectedShippingInfoWrappers.find(
                                                    (wrapper) => wrapper.shipping_info.id === info.id,
                                                )
                                            }
                                        >
                                            Add
                                        </Button>
                                    </TableCell>
                                    <TableCell>{mission.mission_name}</TableCell>
                                    <TableCell>{mission.project_name}</TableCell>
                                    <TableCell>{mission.project_number}</TableCell>
                                    <TableCell>{info.delivery_drive_name}</TableCell>
                                    <TableCell>{info.shipping_number}</TableCell>
                                    <TableCell>{info.office}</TableCell>
                                    <TableCell>{mission.platform?.platform_name}</TableCell>
                                    <TableCell>{mission.flown_date}</TableCell>
                                    <TableCell>{mission.operator_1_name}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                showFirstButton={true}
                showLastButton={true}
                component={"div"}
                rowsPerPageOptions={nexPageSizes}
                count={totalShippingInfoCount ?? -1} // -1 allows for server side pagination for an unknown number of items
                page={page}
                rowsPerPage={pageSize}
                onPageChange={(_, page) => handlePageChange(page)}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
        </Paper>
    )
}

const SortableSelectMissionsTableCell = ({ children, orderBy }: { children: string; orderBy: SelectMissionsOrder }) => {
    const order = useDataReceiptStore((state) => state.order)
    const setOrder = useDataReceiptStore((state) => state.setOrder)

    const activeOrderBy = useDataReceiptStore((state) => state.orderBy)
    const setOrderBy = useDataReceiptStore((state) => state.setOrderBy)

    const isCurrentlySorted = activeOrderBy === orderBy

    const handleSort = () => {
        const isAsc = isCurrentlySorted && order === Order.asc
        setOrder(isAsc ? Order.desc : Order.asc)
        setOrderBy(orderBy)
    }

    return (
        <TableCell sortDirection={isCurrentlySorted ? order : false}>
            <TableSortLabel
                active={isCurrentlySorted}
                direction={isCurrentlySorted ? order : Order.asc}
                onClick={handleSort}
            >
                {children}
            </TableSortLabel>
        </TableCell>
    )
}
